import { utils } from '.';
import Bugsnag, { Event } from '@bugsnag/js';
import axios from 'axios';
import type { ApiErrors, EndpointFailureKind, ErrorResponse } from '@/types/endpoints';
import { isRecord } from './dataUtils';
import { isSessionTransition } from '@/session/operation';
import { sanitizeTelemetryPath } from '@/session/telemetry';

export const print = (err: Error) => {
  let errorLog = `---------------- Error Information ----------------\nError Message: ${err.message}\n`;

  // full error
  errorLog += `---------------- Full Error ----------------\n${err}\n`;

  // log all information in one console.error statement
  console.error(errorLog);
};

export type HandleAxiosErrorOptions = {
  redactRequestData?: boolean;
};

/**
 * Extract a human-readable message from an unknown thrown value, falling back to
 * the provided default when no usable message is present. Use in `catch (err: unknown)`
 * blocks that only need to surface a message (e.g. a toast) rather than a full ErrorResponse.
 */
export const getErrorMessage = (err: unknown, fallback = 'Something went wrong'): string => {
  const message = err instanceof Error ? err.message : '';
  return message || fallback;
};

/**
 * Failure copy that keeps the caller's sentence and appends the server's detail when there is one:
 * `Failed to load users.` or `Failed to load users: <detail>`.
 */
export const errorCopy = (base: string, detail?: string): string => {
  const trimmedDetail = detail?.trim();
  return trimmedDetail ? `${base}: ${trimmedDetail}` : `${base}.`;
};

/**
 * True when an EndpointResponse represents a canceled request (unmount or
 * supersession abort, or a session transition). Canceled responses are
 * teardown noise, not failures — check this and early-return before surfacing
 * `response.errors.error` in a toast.
 */
export const isCanceledResponse = (response: unknown): response is ErrorResponse & { canceled: true } =>
  isRecord(response) && response.isError === true && (response.failureKind === 'canceled' || response.canceled === true);

export const isUnauthenticatedResponse = (response: unknown): response is ErrorResponse =>
  isRecord(response) && response.isError === true && response.failureKind === 'unauthenticated';

export const isDeniedResponse = (response: unknown): response is ErrorResponse =>
  isRecord(response) && response.isError === true && response.failureKind === 'denied';

export const isFailedResponse = (response: unknown): response is ErrorResponse =>
  isRecord(response) && response.isError === true && response.failureKind === 'failed';

/**
 * Tolerant abort detection: a caller-owned AbortController canceling a fetch
 * surfaces as a DOMException named AbortError in browsers but can be a
 * non-Error object with code ABORT_ERR in other runtimes. Both spellings are
 * teardown noise and must never reach Bugsnag as real failures.
 */
export const isAbortError = (err: unknown): boolean => {
  if (!err || typeof err !== 'object') return false;
  const candidate = err as { name?: unknown; code?: unknown };
  return candidate.name === 'AbortError' || candidate.code === 'ABORT_ERR';
};

export const handleAxiosError = (err: unknown, options?: HandleAxiosErrorOptions): ErrorResponse => {
  // Canceled requests come from AbortController-driven supersession/unmount
  // or a session transition, not failures; skip the logger and Bugsnag so
  // they don't bury real errors.
  if (axios.isCancel(err) || isSessionTransition(err) || isAbortError(err)) {
    return {
      isError: true,
      failureKind: 'canceled',
      canceled: true,
      result: undefined,
      status: 0,
      execution_time: 0,
      num_results: 0,
      errors: { error: 'Request canceled' },
    };
  }

  const axiosError = axios.isAxiosError(err) ? err : undefined;
  const message = axiosError?.message || (err instanceof Error ? err.message : 'Unknown error');

  utils.logger.error(`${message}`);

  const status = axiosError?.response?.status ?? 0;
  const failureKind: EndpointFailureKind = status === 401 ? 'unauthenticated' : status === 403 ? 'denied' : 'failed';

  Bugsnag.notify(new Error('Request failed'), (event: Event) => {
    event.addMetadata('request_error', {
      path: sanitizeTelemetryPath(axiosError?.config?.url),
      method: axiosError?.config?.method?.toUpperCase(),
      status,
      failureKind,
      errorCode: typeof axiosError?.code === 'string' ? axiosError.code : undefined,
    });
  });

  const responseData: unknown = axiosError?.response?.data;
  const responseRecord = isRecord(responseData) ? responseData : undefined;

  const responseError = responseRecord && typeof responseRecord.error === 'string' ? responseRecord.error : message;

  const executionTime = responseRecord && typeof responseRecord.execution_time === 'number' ? responseRecord.execution_time : 0;
  const numResults = responseRecord && typeof responseRecord.num_results === 'number' ? responseRecord.num_results : 0;

  const existingErrors = responseRecord && isRecord(responseRecord.errors) ? responseRecord.errors : undefined;
  const errors: ApiErrors = existingErrors
    ? {
        ...existingErrors,
        error: typeof existingErrors.error === 'string' ? existingErrors.error : responseError,
      }
    : { error: responseError };

  return {
    ...(responseRecord ?? {}),
    isError: true as const,
    failureKind,
    // Cancellation is a client-side fact set only in the early return above;
    // a server body carrying `canceled: true` must not survive the spread, or
    // isCanceledResponse would silently swallow a real failure.
    canceled: undefined,
    result: undefined,
    status,
    execution_time: executionTime,
    num_results: numResults,
    errors,
  };
};
